import { differenceBy } from 'lodash';
import type {
    ColumnDefinitionEnumType,
    WorkspaceQueueColumnDto,
    WorkspaceQueueDto,
} from '@mark43/cad-api';
import { observable, makeObservable, action, computed } from 'mobx';
import columnDefinitionEnum, {
    ConcreteColumnDefinitionData,
} from '../queues/column-definition.enum';
import type { Optional } from '../queues/types';
import { assertNonNull } from '../queues/utils/assert-non-null';
import { PanelActionDelegate } from './workspace-panel';
import { WorkspaceQueueColumn } from './workspace-queue-column';
import { EVENT_UPDATE_NOTIFICATION_MODE } from './constants';

type WorkspaceQueueDtoWithOptionalColumnFields = Omit<WorkspaceQueueDto, 'queueColumns'> & {
    queueColumns: WorkspaceQueueColumn[];
};

export type WorkspaceQueueDelegate = Omit<PanelActionDelegate, 'enqueueSaveWorkspaceWindow'> & {
    enqueueSaveWorkspaceWindow: () => void;
};

export class WorkspaceQueue implements WorkspaceQueueDtoWithOptionalColumnFields {
    id: WorkspaceQueueDto['id'];
    dataEntityType: WorkspaceQueueDto['dataEntityType'];
    @observable
    assignmentStatus: WorkspaceQueueDto['assignmentStatus'];
    @observable
    displayDensity: WorkspaceQueueDto['displayDensity'];
    eventQueue: WorkspaceQueueDto['eventQueue'];
    @observable
    groupByQueueColumnDefinition: WorkspaceQueueDto['groupByQueueColumnDefinition'];
    @observable
    groupBySortDirection: WorkspaceQueueDto['groupBySortDirection'];
    @observable
    pinBackupsToPrimaryUnits: WorkspaceQueueDto['pinBackupsToPrimaryUnits'];
    @observable
    hospitalCapabilityFilters: WorkspaceQueueDto['hospitalCapabilityFilters'];
    @observable
    hospitalStatusFilters: WorkspaceQueueDto['hospitalStatusFilters'];
    /**
     * @Deprecated this (unitTypeFilters) is now deprecated, unitQueueColumnFilters will now contain the values of unitTypeFilters
     */
    @observable
    unitTypeFilters: WorkspaceQueueDto['unitTypeFilters'];
    @observable
    unitQueueColumnFilters: WorkspaceQueueDto['unitQueueColumnFilters'];
    @observable
    eventQueueFilters: WorkspaceQueueDto['eventQueueFilters'];
    @observable
    queueClasses: WorkspaceQueueDto['queueClasses'];
    @observable
    queueColumns: WorkspaceQueueColumn[];
    @observable
    showUpdates: WorkspaceQueueDto['showUpdates'];
    @observable
    fullRowUpdates: WorkspaceQueueDto['fullRowUpdates'];
    @observable
    fullRowUnitTimerUpdates: WorkspaceQueueDto['fullRowUnitTimerUpdates'];
    @observable
    sortByQueueColumnDefinition: WorkspaceQueueDto['sortByQueueColumnDefinition'];
    @observable
    sortDirection: WorkspaceQueueDto['sortDirection'];
    delegate: WorkspaceQueueDelegate;

    constructor(configuration: WorkspaceQueueDto, delegate: WorkspaceQueueDelegate) {
        this.id = configuration.id;
        this.dataEntityType = configuration.dataEntityType;
        this.assignmentStatus = configuration.assignmentStatus;
        this.displayDensity = configuration.displayDensity;
        this.eventQueue = configuration.eventQueue;
        this.groupByQueueColumnDefinition = configuration.groupByQueueColumnDefinition;
        this.groupBySortDirection = configuration.groupBySortDirection;
        this.pinBackupsToPrimaryUnits = configuration.pinBackupsToPrimaryUnits;
        this.unitTypeFilters = configuration.unitTypeFilters;
        this.eventQueueFilters = configuration.eventQueueFilters;
        this.unitQueueColumnFilters = configuration.unitQueueColumnFilters;
        this.hospitalStatusFilters = configuration.hospitalStatusFilters;
        this.hospitalCapabilityFilters = configuration.hospitalCapabilityFilters;
        this.queueClasses = configuration.queueClasses;
        this.showUpdates = configuration.showUpdates;
        this.fullRowUpdates = configuration.fullRowUpdates;
        this.fullRowUnitTimerUpdates = configuration.fullRowUnitTimerUpdates;
        this.sortByQueueColumnDefinition = configuration.sortByQueueColumnDefinition;
        this.sortDirection = configuration.sortDirection;
        this.queueColumns = [];
        this.delegate = delegate;

        this.availableColumns = Object.values(
            columnDefinitionEnum
        ).filter((columnDefinitionEnumValue) =>
            (columnDefinitionEnumValue.validEntityTypes as readonly string[]).includes(
                configuration.dataEntityType
            )
        );

        this.columnsWithPotentialNotifications = this.availableColumns.filter(
            (columnDefinitionData) => {
                return (
                    'eventNotificationNames' in columnDefinitionData &&
                    columnDefinitionData.eventNotificationNames &&
                    columnDefinitionData.eventNotificationNames.length > 0
                );
            }
        );

        this.validColumnsMap = this.availableColumns.reduce((acc, columnDefinitionEnumValue) => {
            acc[columnDefinitionEnumValue.name] = true;
            return acc;
        }, {} as Record<string, boolean>);

        for (const queueColumn of configuration.queueColumns.slice().sort((a, b) => {
            return a.columnPosition < b.columnPosition ? -1 : 1;
        })) {
            // ensure that we have a column definition for every provided column
            assertNonNull(
                columnDefinitionEnum[queueColumn.columnDefinition],
                `Unexpectedly did not find definition for column ${queueColumn.columnDefinition}`
            );
            this.createColumn(queueColumn);
        }

        makeObservable(this);
    }

    public getDto(): WorkspaceQueueDto {
        return {
            id: this.id,
            dataEntityType: this.dataEntityType,
            displayDensity: this.displayDensity,
            unitTypeFilters: this.unitTypeFilters,
            queueClasses: this.queueClasses,
            queueColumns: this.queueColumns.map((queueColumn, index) => ({
                ...queueColumn.getDto(),
                columnPosition: index + 1,
            })),
            hospitalCapabilityFilters: this.hospitalCapabilityFilters,
            hospitalStatusFilters: this.hospitalStatusFilters,
            unitQueueColumnFilters: this.unitQueueColumnFilters,
            eventQueueFilters: this.eventQueueFilters,
            assignmentStatus: this.assignmentStatus,
            eventQueue: this.eventQueue,
            groupByQueueColumnDefinition: this.groupByQueueColumnDefinition,
            groupBySortDirection: this.groupBySortDirection,
            pinBackupsToPrimaryUnits: this.pinBackupsToPrimaryUnits,
            showUpdates: this.showUpdates,
            fullRowUpdates: this.fullRowUpdates,
            fullRowUnitTimerUpdates: this.fullRowUnitTimerUpdates,
            sortByQueueColumnDefinition: this.sortByQueueColumnDefinition,
            sortDirection: this.sortDirection,
        };
    }

    @action
    setSortColumn(
        definition?: ColumnDefinitionEnumType,
        options?: { skipDefaultSort?: boolean }
    ): void {
        if (this.sortByQueueColumnDefinition === definition) {
            if (this.sortDirection === 'REGULAR_DESCENDING' && !options?.skipDefaultSort) {
                this.sortByQueueColumnDefinition = undefined;
            } else if (this.sortDirection === 'REGULAR_ASCENDING') {
                this.sortDirection = 'REGULAR_DESCENDING';
            }
        } else {
            this.sortByQueueColumnDefinition = definition;
            this.sortDirection = 'REGULAR_ASCENDING';
        }
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @action
    setSortDirection(direction: WorkspaceQueueDto['sortDirection']): void {
        this.sortDirection = direction;
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @computed
    get sortDirectionIsAscending(): boolean {
        return this.sortDirection === 'REGULAR_ASCENDING';
    }

    @action
    setGroupByColumn(definition?: ColumnDefinitionEnumType): void {
        if (definition === this.groupByQueueColumnDefinition) {
            this.groupByQueueColumnDefinition = undefined;
        } else {
            this.groupByQueueColumnDefinition = definition;
        }
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @action
    setGroupBySortDirection(direction: WorkspaceQueueDto['groupBySortDirection']): void {
        this.groupBySortDirection = direction;
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @computed
    get groupBySortDirectionIsAscending(): boolean {
        return this.groupBySortDirection === 'REGULAR_ASCENDING';
    }

    /**
     * Columns indexed by their key for fast updates
     */
    public readonly columnsByColumnDefinitionName: Partial<
        Record<ColumnDefinitionEnumType, WorkspaceQueueColumn>
    > = {};

    /**
     * List of all available columns for the entity type of this queue
     */
    public readonly availableColumns: ConcreteColumnDefinitionData[];

    /**
     * List of all available columns for the entity type of this queue
     */
    public readonly columnsWithPotentialNotifications: ConcreteColumnDefinitionData[];

    /**
     * Lookup map used to ensure that a column definition is valid for the queue's entity type
     */
    private validColumnsMap: Record<string, boolean>;

    /**
     * Updates the width of a given column.
     */
    public setColumnWidth(columnDefinitionName: ColumnDefinitionEnumType, newWidth: number): void {
        this.columnsByColumnDefinitionName[columnDefinitionName]?.setWidth(newWidth);
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @action
    public updateEventUpdatesNotificationMode(
        mode: keyof typeof EVENT_UPDATE_NOTIFICATION_MODE
    ): void {
        // BE saves `showUpdates` and `fullRowUpdates` as boolean. Combine these 2 boolean for 3 modes
        switch (mode) {
            case EVENT_UPDATE_NOTIFICATION_MODE.NONE:
                this.showUpdates = false;
                this.fullRowUpdates = false;
                break;
            case EVENT_UPDATE_NOTIFICATION_MODE.BADGE:
                this.showUpdates = true;
                this.fullRowUpdates = false;
                break;
            case EVENT_UPDATE_NOTIFICATION_MODE.FULL_ROW:
                this.showUpdates = true;
                this.fullRowUpdates = true;
                break;
            default:
        }
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    @computed
    get currentEventUpdatesNotificationMode(): keyof typeof EVENT_UPDATE_NOTIFICATION_MODE {
        if (this.showUpdates && this.fullRowUpdates) {
            return EVENT_UPDATE_NOTIFICATION_MODE.FULL_ROW;
        }
        if (this.showUpdates && !this.fullRowUpdates) {
            return EVENT_UPDATE_NOTIFICATION_MODE.BADGE;
        }
        return EVENT_UPDATE_NOTIFICATION_MODE.NONE;
    }

    /**
     * Whether rows show the fixed gutter column (see QueueRowGutter), which only holds the event
     * update badges and the pin-to-primary bracket.
     */
    @computed
    get showsRowGutter(): boolean {
        const isUnitQueue = this.dataEntityType === 'UNIT';
        const showsUpdateBadges =
            this.showUpdates && (this.dataEntityType === 'CAD_AGENCY_EVENT' || isUnitQueue);
        return showsUpdateBadges || (isUnitQueue && !!this.pinBackupsToPrimaryUnits);
    }

    @action
    public toggleFullRowUnitTimerUpdates(): void {
        this.fullRowUnitTimerUpdates = !this.fullRowUnitTimerUpdates;
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    /**
     * Moves a given column to a specified position
     */
    @action
    public setColumnOrder(columnDefinition: string, newPosition: number): void {
        const currentIndex = this.queueColumns.findIndex(
            (x) => x.columnDefinition === columnDefinition
        );
        this.queueColumns.splice(newPosition, 0, this.queueColumns[currentIndex]);
        this.queueColumns.splice(currentIndex > newPosition ? currentIndex + 1 : currentIndex, 1);
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    /**
     * Adds a new column to the end of the current list of columns
     */
    @action
    public addColumn(
        data: Optional<WorkspaceQueueColumnDto, 'width' | 'queueId' | 'columnPosition' | 'id'>
    ): void {
        this.createColumn(data);
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    /**
     * Adds non-existent columns from `data` to the current list of columns and will remove
     * columns not within `data` from the current list.
     */
    @action
    public updateColumns(
        columnDefinitions: Optional<
            WorkspaceQueueColumnDto,
            'width' | 'queueId' | 'columnPosition' | 'id'
        >[]
    ): void {
        const nonExistingColumns = differenceBy(
            columnDefinitions,
            this.queueColumns,
            (x) => x.columnDefinition
        );
        const columnsToRemove = differenceBy(
            this.queueColumns,
            columnDefinitions,
            (x) => x.columnDefinition
        );

        for (const columnDefinition of columnsToRemove) {
            const index = this.queueColumns.findIndex(
                (column) => column.columnDefinition === columnDefinition.columnDefinition
            );
            delete this.columnsByColumnDefinitionName[columnDefinition.columnDefinition];
            if (index > -1) {
                this.queueColumns.splice(index, 1);
            }
        }

        for (const columnDefinition of nonExistingColumns) {
            const newColumn = this.createColumn(columnDefinition);
            this.columnsByColumnDefinitionName[columnDefinition.columnDefinition] = newColumn;
        }
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    /**
     *  Removes a column from the current list of columns
     */
    @action
    public removeColumn(definition: ColumnDefinitionEnumType): void {
        const index = this.queueColumns.findIndex(
            (column) => column.columnDefinition === definition
        );
        if (index > -1) {
            this.queueColumns.splice(index, 1);
            delete this.columnsByColumnDefinitionName[definition];
        }
        this.delegate.enqueueSaveWorkspaceWindow();
    }

    private createColumn(
        data: Optional<WorkspaceQueueColumnDto, 'width' | 'queueId' | 'columnPosition' | 'id'>
    ): WorkspaceQueueColumn {
        assertNonNull(
            this.validColumnsMap[data.columnDefinition],
            `Column "${data.columnDefinition} is not valid for queue with entity type "${this.dataEntityType}"`
        );
        const columnDefinitionData = columnDefinitionEnum[data.columnDefinition];
        const column = new WorkspaceQueueColumn(
            {
                ...data,
                queueId: this.id,
                width: data.width ?? columnDefinitionData.defaultWidth,
            },
            this.delegate
        );
        this.columnsByColumnDefinitionName[column.columnDefinition] = column;
        this.queueColumns.push(column);
        this.delegate.enqueueSaveWorkspaceWindow();
        return column;
    }
}
